import type { SchoolDetailsFormData } from './school.schemas';

type SchoolUpdatePayload = Omit<
    SchoolDetailsFormData,
    'saptamanaVerdeStart' | 'saptamanaVerdeEnd' | 'saptamanaVerdeNecunoscuta'
> & {
    id: string;
    saptamanaVerdeStart: string | null;
    saptamanaVerdeEnd: string | null;
};

/**
 * Construieste corpul lui `PATCH /api/schools` din scoala INCARCATA plus valorile editabile.
 *
 * 🔴 De ce exista (issue #15, al patrulea strat): formularul de detalii ale scolii nu trimitea
 * `id`, deci backendul nu avea pe cine sa actualizeze. `SchoolService.update()` face
 * `schoolRepository.findById(dto.getId())` cu `null` -> `InvalidDataAccessApiUsageException`,
 * zero scriere. Endpointul e `PATCH /api/schools`, fara parametru de cale: **identitatea scolii
 * vine DOAR din corp**.
 *
 * Cauza e o consecinta tacuta a rescrierii pe Zod din `c083478c` (2025-11-02). Hook-ul de dinainte
 * tinea starea formularului ca obiect liber si o seed-uia cu scoala intreaga
 * (`setFormData(data)`), deci `id` pleca de la sine. `schoolDetailsSchema` listeaza doar cele
 * cinci campuri editabile, iar `z.object()` **elimina cheile necunoscute** la parsare — asa a
 * disparut `id`, fara nicio eroare de compilare si fara ca vreun test sa spuna ceva.
 * Formularul e rupt de atunci.
 *
 * ⚠️ **`id` NU se pune in `schoolDetailsSchema`.** Schema descrie ce poate EDITA utilizatorul, si
 * identitatea nu e editabila — pusa acolo, ar deveni un camp pe care un client rau intentionat il
 * poate schimba ca sa scrie in alta scoala. Garda de proprietate din backend
 * (`verificaProprietateaScolii`, adaugata la #20) ar refuza, dar frontendul n-are de ce sa trimita
 * o valoare pe care utilizatorul o controleaza. `id`-ul vine din scoala incarcata prin
 * `GET /api/schools/own`.
 *
 * Ordinea conteaza: `...valoriEditate` vine DUPA, ca sa nu poata un camp din `schoolData` sa
 * suprascrie ce a introdus utilizatorul.
 *
 * „Saptamana verde" pleaca MEREU, cu `null` cand e goala (b35, 2026-09-15): schema transforma sirul
 * gol in `undefined`, iar `JSON.stringify` omite cheile `undefined` — backendul ar citi „omis =
 * neschimbat" si un interval sters din formular ar ramane tacut pe scoala. `null` pe ambele e
 * golirea explicita.
 *
 * @param schoolData scoala incarcata din backend (sursa lui `id`)
 * @param valoriEditate valorile validate ale formularului
 * @returns corpul de trimis, sau `null` daca scoala nu s-a incarcat inca / n-are `id` — caz in
 *   care apelantul NU trebuie sa trimita nimic: un PATCH fara `id` e garantat esuat.
 */
export function buildSchoolUpdatePayload(
    schoolData: { id?: string } | null | undefined,
    valoriEditate: SchoolDetailsFormData
): SchoolUpdatePayload | null {
    const id = schoolData?.id;
    if (!id) return null;

    // `saptamanaVerdeNecunoscuta` e UI-only (SE-10/11) -- n-are corespondent in DTO, deci
    // NU pleaca in corp, altfel ar ajunge ca proprietate necunoscuta la backend.
    const { saptamanaVerdeNecunoscuta: _saptamanaVerdeNecunoscuta, ...editabile } = valoriEditate;

    return {
        id,
        ...editabile,
        saptamanaVerdeStart: valoriEditate.saptamanaVerdeStart ?? null,
        saptamanaVerdeEnd: valoriEditate.saptamanaVerdeEnd ?? null,
    };
}
